import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as ExecuteIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toolService } from '../services/toolService';
import { useContextStore } from '../store/contextStore';
import { useNotificationStore } from '../store/notificationStore';
import { ToolCategoryIcon } from '../components/tools/ToolCategoryIcon';
import { executionLocationDisplayLabel, type ToolDto, type ToolFilters, type ToolRiskLevel } from '../types/tool';
import { TextField } from '../@core/components/mui';
import DataTable, { type DataTableColumn } from '../components/data-display/DataTable';
import { PageListLayout } from '../components/layouts/PageListLayout';

const riskLevelLabel = (risk: ToolRiskLevel) => {
  switch (risk) {
    case 0:
      return 'Low';
    case 1:
      return 'Medium';
    case 2:
      return 'High';
    case 3:
      return 'Critical';
    default:
      return 'Unknown';
  }
};

const riskLevelColor = (risk: ToolRiskLevel) => {
  switch (risk) {
    case 0:
      return 'success' as const;
    case 1:
      return 'warning' as const;
    case 2:
      return 'error' as const;
    case 3:
      return 'error' as const;
    default:
      return 'default' as const;
  }
};

const categoryIcon = (category: string) => <ToolCategoryIcon category={category} fontSize="small" />;

/**
 * Tools list page component.
 */
export const ToolsListPage = () => {
  const navigate = useNavigate();
  const { addNotification } = useNotificationStore();
  const currentTenant = useContextStore((s) => s.currentTenant);
  const currentProject = useContextStore((s) => s.currentProject);

  const [tools, setTools] = useState<ToolDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);

  const [filters, setFilters] = useState<ToolFilters>({
    pageNumber: 1,
    pageSize: 10,
    searchTerm: '',
    category: '',
    isDefault: undefined,
    isActive: true,
  });

  const categories = useMemo(() => {
    const set = new Set<string>();
    tools.forEach((t) => t.category && set.add(t.category));
    return Array.from(set).sort();
  }, [tools]);

  const loadTools = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await toolService.getTools(filters);
      setTools(result.items);
      setTotalCount(result.totalCount);
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to load tools';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTools();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.pageNumber, filters.pageSize, filters.searchTerm, filters.category, filters.isDefault, filters.isActive, currentTenant?.id, currentProject?.id]);

  const handleDelete = async (tool: ToolDto) => {
    if (!window.confirm(`Are you sure you want to delete "${tool.displayName}"?`)) return;
    try {
      await toolService.deleteTool(tool.id);
      addNotification('Tool deleted successfully', 'success');
      await loadTools();
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to delete tool', 'error');
    }
  };

  // Define table columns
  const tableColumns: DataTableColumn<ToolDto>[] = [
    {
      id: 'name',
      label: 'Name',
      minWidth: 200,
      render: (tool) => (
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {tool.displayName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {tool.name}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'category',
      label: 'Category',
      minWidth: 150,
      render: (tool) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {categoryIcon(tool.category)}
          <Typography variant="body2">{tool.category}</Typography>
        </Box>
      ),
    },
    {
      id: 'type',
      label: 'Type',
      minWidth: 100,
      render: (tool) => (
        <Chip
          label={tool.isDefault ? 'Default' : 'Custom'}
          size="small"
          color={tool.isDefault ? 'primary' : 'default'}
          variant={tool.isDefault ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      id: 'execution',
      label: 'Runs on',
      minWidth: 110,
      render: (tool) => (
        <Chip label={executionLocationDisplayLabel(tool.executionLocation)} size="small" variant="outlined" />
      ),
    },
    {
      id: 'risk',
      label: 'Risk',
      minWidth: 100,
      render: (tool) => (
        <Chip label={riskLevelLabel(tool.riskLevel)} size="small" color={riskLevelColor(tool.riskLevel)} />
      ),
    },
    {
      id: 'approval',
      label: 'Approval',
      minWidth: 120,
      render: (tool) => (
        <Chip
          label={tool.requiresApproval ? 'Required' : 'Not required'}
          size="small"
          color={tool.requiresApproval ? 'warning' : 'default'}
          variant={tool.requiresApproval ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 100,
      render: (tool) => (
        <Chip
          label={tool.isActive ? 'Active' : 'Inactive'}
          size="small"
          color={tool.isActive ? 'success' : 'default'}
          variant={tool.isActive ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      minWidth: 180,
      align: 'right',
      render: (tool) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
          <Tooltip title="View">
            <IconButton size="small" onClick={() => navigate(`/tools/${tool.id}`)}>
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => navigate(`/tools/${tool.id}/edit`)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Execute">
            <IconButton size="small" onClick={() => navigate(`/tools/${tool.id}`)}>
              <ExecuteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => handleDelete(tool)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <PageListLayout
      title="Tools"
      createButton={{ label: 'Create Tool', onClick: () => navigate('/tools/new'), icon: <AddIcon /> }}
      filters={
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <TextField
            label="Search tools"
            size="small"
            value={filters.searchTerm || ''}
            onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value, pageNumber: 1 })}
            sx={{ flexGrow: 1, minWidth: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={filters.category || ''}
              label="Category"
              onChange={(e) => setFilters({ ...filters, category: (e.target.value as string) || undefined, pageNumber: 1 })}
            >
              <MenuItem value="">All Categories</MenuItem>
              {categories.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={filters.isDefault === undefined ? '' : filters.isDefault ? 'default' : 'custom'}
              label="Type"
              onChange={(e) => {
                const value = e.target.value as string;
                setFilters({
                  ...filters,
                  isDefault: value === '' ? undefined : value === 'default',
                  pageNumber: 1,
                });
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="default">Default</MenuItem>
              <MenuItem value="custom">Custom</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.isActive === undefined ? '' : filters.isActive ? 'active' : 'inactive'}
              label="Status"
              onChange={(e) => {
                const value = e.target.value as string;
                setFilters({
                  ...filters,
                  isActive: value === '' ? undefined : value === 'active',
                  pageNumber: 1,
                });
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Box>
      }
      loading={loading}
      error={error || undefined}
      onErrorDismiss={() => setError(null)}
      isEmpty={tools.length === 0}
      emptyState={{
        title: 'No tools found',
        description: 'Try adjusting your filters or create a new tool to get started.',
        action: { label: 'Create Tool', onClick: () => navigate('/tools/new') },
      }}
      pagination={{
        page: filters.pageNumber || 1,
        totalPages: Math.ceil(totalCount / (filters.pageSize || 10)),
        onChange: (page) => setFilters({ ...filters, pageNumber: page }),
      }}
    >
      <DataTable
        columns={tableColumns}
        rows={tools}
        loading={false}
      />
    </PageListLayout>
  );
};

